/*
 * SSE transport, hand-rolled over `fetch`.
 *
 * `EventSource` cannot be used here and this is not a preference: the stream
 * endpoint requires an `Authorization` header, and `EventSource` has no API for
 * request headers at all. (Some documentation in this installation still shows an
 * `EventSource` example for this endpoint; it cannot work.) In this app the bearer
 * is attached by the BFF rather than by the browser, but the fetch reader is still
 * required — the transport also needs to read the response STATUS before deciding
 * whether to consume a body, which `EventSource` never exposes.
 *
 * What the contract requires a client to get right, and what this does about it:
 *
 *  - `409` at connect means the run already reached a terminal state. The stream is
 *    live-only, never a replay log, so this is not an error: read durable state
 *    instead. Reported as `terminal`.
 *  - `410` means the resume cursor fell out of retained history. Reported as
 *    `cursor_lost`; the caller reloads durable messages.
 *  - `401` means the session died mid-stream. Reported as `unauthorized`.
 *  - `id:` values are Redis stream cursors and are replayed as `Last-Event-ID` on
 *    reconnect. Heartbeats arrive as `:` comment lines with NO id — they prove
 *    liveness and must not advance the cursor.
 *  - A dropped connection is not itself a failure signal: the run may have finished
 *    normally. The caller re-checks run state before deciding anything.
 *  - A stream can die WITHOUT an error and without EOF — see the watchdog below.
 */

export interface StreamEnvelope {
  schema_version: number
  event_id: string
  event_type: string
  sequence_number: number
  session_id: string
  run_id: string
  root_run_id: string
  stream_scope: 'root' | 'subagent' | 'system'
  root_terminal: boolean
  payload: Record<string, unknown>
}

export type StreamEnd =
  | { reason: 'terminal' }
  | { reason: 'cursor_lost' }
  | { reason: 'unauthorized' }
  | { reason: 'closed' }
  | { reason: 'error'; status?: number; message: string }

export interface StreamOptions {
  url: string
  /** Resume cursor (`<ms>-<seq>`), or null to start from the current tip. */
  lastEventId?: string | null
  onEvent: (envelope: StreamEnvelope) => void
  /** Fires on every heartbeat as well as every event — a liveness tick. */
  onAlive?: () => void
  onEnd: (end: StreamEnd) => void
  /**
   * Server heartbeat cadence in ms, which sets the stall threshold. Override only
   * if the deployment's `sse_heartbeat_interval_seconds` differs from the default.
   */
  heartbeatIntervalMs?: number
}

const TERMINAL_EVENT_TYPES = new Set([
  'run_completed',
  'run_failed',
  'run_cancelled',
  'run_partial',
])

const RECONNECT_BASE_MS = 500
const RECONNECT_CAP_MS = 8_000
const MAX_RECONNECT_ATTEMPTS = 5

/**
 * The server's own heartbeat cadence: `RuntimeConstants.sse_heartbeat_interval_seconds`,
 * default 15s. A healthy stream refreshes the liveness clock at least this often,
 * which is the only reason silence is measurable at all.
 *
 * Measured rather than assumed, because the whole watchdog rests on it: a run held
 * silent by a 75-second shell command produced 6 heartbeat comments at a steady
 * 15.0s cadence, and the largest gap between any two signals over the entire run was
 * 15.0s. Heartbeats are NOT suppressed while a run is mid-tool-call — the server
 * emits one whenever nothing else has arrived within the interval, with no idleness
 * or run-state condition — so a long tool call cannot be mistaken for a dead stream.
 *
 * It is a MIRROR, and mirrors go stale: no public endpoint reports the effective
 * interval, so lowering the constant server-side leaves this watchdog killing healthy
 * streams with nothing here able to notice. Recorded as an API gap (`API-GAPS.md`
 * §2.14); the fix belongs upstream, in a value the stream itself states.
 */
const SERVER_HEARTBEAT_INTERVAL_MS = 15_000

/**
 * Silence tolerated before the connection is presumed dead, as a multiple of the
 * heartbeat interval. Two consecutive missed heartbeats plus scheduling jitter is
 * comfortably survivable at 3x, which leaves 3x headroom over the worst gap actually
 * observed (15.0s). At 2x a single late heartbeat on a slow network would tear down a
 * perfectly good stream.
 */
const STALL_HEARTBEAT_MULTIPLE = 3

/** Detection latency is bounded by the tick, so poll well inside one interval. */
const WATCHDOG_TICK_DIVISOR = 3

/**
 * A single run's event stream. `start()` resolves once the stream has ended for
 * any reason; `close()` stops it and reports `closed`.
 */
export class RunStream {
  private readonly options: StreamOptions
  private readonly stallThresholdMs: number
  private readonly watchdogTickMs: number
  private controller: AbortController | null = null
  private lastEventId: string | null
  private attempt = 0
  private stopped = false
  private ended = false
  /** Wall clock of the last event or heartbeat; 0 before the first open. */
  private lastAliveAt = 0
  private watchdog: ReturnType<typeof setInterval> | null = null
  /**
   * True when the abort now in flight came from the watchdog rather than from
   * `close()`. It separates "the caller stopped listening" (end) from "the
   * connection went silent" (reconnect) — both surface as the same `AbortError`.
   */
  private stalled = false

  constructor(options: StreamOptions) {
    this.options = options
    this.lastEventId = options.lastEventId ?? null
    const heartbeat = options.heartbeatIntervalMs ?? SERVER_HEARTBEAT_INTERVAL_MS
    this.stallThresholdMs = heartbeat * STALL_HEARTBEAT_MULTIPLE
    this.watchdogTickMs = Math.max(1, Math.floor(heartbeat / WATCHDOG_TICK_DIVISOR))
  }

  close(): void {
    this.stopped = true
    this.stopWatchdog()
    this.controller?.abort()
    this.controller = null
    this.end({ reason: 'closed' })
  }

  async start(): Promise<void> {
    while (!this.stopped) {
      const outcome = await this.openOnce()
      if (outcome.kind === 'ended') {
        this.end(outcome.end)
        return
      }
      // Transport-level drop. The run may simply have finished, so hand control
      // back after a bounded number of attempts rather than reconnecting forever.
      this.attempt += 1
      if (this.attempt > MAX_RECONNECT_ATTEMPTS) {
        this.end({ reason: 'error', message: 'stream dropped and did not recover' })
        return
      }
      await sleep(Math.min(RECONNECT_CAP_MS, RECONNECT_BASE_MS * 2 ** (this.attempt - 1)))
    }
  }

  private end(end: StreamEnd): void {
    if (this.ended) return
    this.ended = true
    this.stopWatchdog()
    this.options.onEnd(end)
  }

  /** Record a sign of life — an event or a heartbeat comment — and notify. */
  private markAlive(): void {
    this.lastAliveAt = Date.now()
    this.options.onAlive?.()
  }

  /**
   * Liveness watchdog for the failure the fetch reader cannot see.
   *
   * A frozen or backgrounded tab, a sleeping device, or a half-open socket with no
   * FIN produces neither an error nor further bytes: `reader.read()` simply never
   * settles. Nothing throws, EOF never arrives, and so none of the recovery paths
   * above ever run — the stream is dead and the UI waits forever. The watchdog is
   * the only thing that turns that silence into a signal: past the stall threshold
   * it aborts the connection, which surfaces as an abort in `consume` and re-enters
   * the ordinary reconnect-and-reconcile path from the resume cursor.
   *
   * On a resumed tab the interval fires immediately and detects the stall at once,
   * which is precisely the mobile case.
   */
  private startWatchdog(): void {
    if (this.watchdog !== null || typeof setInterval !== 'function') return
    this.watchdog = setInterval(() => {
      if (this.stopped || this.ended || this.lastAliveAt === 0) return
      if (Date.now() - this.lastAliveAt <= this.stallThresholdMs) return
      // Presumed dead. Abort so the read settles; the loop reconnects from the
      // cursor, and a run that actually finished is caught by the 409 on reconnect.
      this.stalled = true
      this.controller?.abort()
    }, this.watchdogTickMs)
    // Never let the timer hold a Node process open (harmless in a browser).
    const handle = this.watchdog as unknown as { unref?: () => void }
    handle.unref?.()
  }

  private stopWatchdog(): void {
    if (this.watchdog !== null) {
      clearInterval(this.watchdog)
      this.watchdog = null
    }
  }

  /** Read and clear the stall flag: was the abort in flight the watchdog's? */
  private consumeStall(): boolean {
    if (!this.stalled) return false
    this.stalled = false
    return true
  }

  private async openOnce(): Promise<{ kind: 'ended'; end: StreamEnd } | { kind: 'dropped' }> {
    this.stopWatchdog()
    this.controller = new AbortController()
    const headers: Record<string, string> = { Accept: 'text/event-stream' }
    if (this.lastEventId) headers['Last-Event-ID'] = this.lastEventId

    let response: Response
    try {
      response = await fetch(this.options.url, {
        method: 'GET',
        headers,
        credentials: 'same-origin',
        signal: this.controller.signal,
      })
    } catch (err) {
      if (this.stopped) return { kind: 'ended', end: { reason: 'closed' } }
      if (this.consumeStall()) return { kind: 'dropped' }
      return err instanceof DOMException && err.name === 'AbortError'
        ? { kind: 'ended', end: { reason: 'closed' } }
        : { kind: 'dropped' }
    }

    if (response.status === 409) return { kind: 'ended', end: { reason: 'terminal' } }
    if (response.status === 410) return { kind: 'ended', end: { reason: 'cursor_lost' } }
    if (response.status === 401) return { kind: 'ended', end: { reason: 'unauthorized' } }
    if (!response.ok || !response.body) {
      return {
        kind: 'ended',
        end: { reason: 'error', status: response.status, message: `stream refused (${response.status})` },
      }
    }

    this.attempt = 0
    this.markAlive()
    this.startWatchdog()

    let sawTerminal = false
    try {
      sawTerminal = await this.consume(response.body)
    } catch (err) {
      if (this.stopped) return { kind: 'ended', end: { reason: 'closed' } }
      // A watchdog abort is a dead connection to be retried, not a caller stop.
      if (this.consumeStall()) return { kind: 'dropped' }
      if (err instanceof DOMException && err.name === 'AbortError') {
        return { kind: 'ended', end: { reason: 'closed' } }
      }
      return { kind: 'dropped' }
    }

    if (sawTerminal) return { kind: 'ended', end: { reason: 'terminal' } }
    // Clean EOF with no terminal event: the server closed the socket. Reconnect
    // from the cursor; the caller reconciles against run state either way.
    return { kind: 'dropped' }
  }

  /** Read the body, framing events. Returns true if a terminal event arrived. */
  private async consume(body: ReadableStream<Uint8Array>): Promise<boolean> {
    const reader = body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let frame = newFrame()
    let sawTerminal = false

    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      const lines = buffer.split('\n')
      // The trailing element is an incomplete line; keep it for the next chunk.
      buffer = lines.pop() ?? ''

      for (const rawLine of lines) {
        const line = rawLine.replace(/\r$/, '')

        if (line === '') {
          if (frame.data.length > 0 && this.dispatch(frame)) sawTerminal = true
          frame = newFrame()
          continue
        }
        if (line.startsWith(':')) {
          // Heartbeat comment: liveness only, carries no id, never a cursor. It is
          // what keeps the watchdog quiet through a long tool call with no output.
          this.markAlive()
          continue
        }
        const colon = line.indexOf(':')
        const field = colon === -1 ? line : line.slice(0, colon)
        const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '')
        if (field === 'event') frame.event = value
        else if (field === 'id') frame.id = value
        else if (field === 'data') frame.data.push(value)
        // Unknown fields are ignored, per the SSE spec.
      }
    }
    return sawTerminal
  }

  /** Parse and hand one framed event to the consumer. Returns true if terminal. */
  private dispatch(frame: Frame): boolean {
    let parsed: unknown
    try {
      parsed = JSON.parse(frame.data.join('\n'))
    } catch {
      return false
    }
    const envelope = asEnvelope(parsed)
    if (!envelope) return false
    if (frame.id) this.lastEventId = frame.id
    this.markAlive()
    this.options.onEvent(envelope)
    return envelope.root_terminal && TERMINAL_EVENT_TYPES.has(envelope.event_type)
  }
}

interface Frame {
  event: string | null
  id: string | null
  data: string[]
}

function newFrame(): Frame {
  return { event: null, id: null, data: [] }
}

/**
 * Accept only envelopes carrying the fields the contract guarantees. A payload
 * that fails this is dropped rather than passed on half-typed.
 */
export function asEnvelope(value: unknown): StreamEnvelope | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const candidate = value as Partial<StreamEnvelope>
  if (candidate.schema_version !== 1) return null
  if (typeof candidate.event_id !== 'string' || candidate.event_id.length === 0) return null
  if (typeof candidate.event_type !== 'string' || candidate.event_type.length === 0) return null
  if (typeof candidate.sequence_number !== 'number') return null
  if (typeof candidate.run_id !== 'string' || candidate.run_id.length === 0) return null
  if (typeof candidate.root_terminal !== 'boolean') return null
  if (!candidate.payload || typeof candidate.payload !== 'object') return null
  return candidate as StreamEnvelope
}

/**
 * Pull one delta fragment out of an envelope.
 *
 * The wire carries streamed content as `content_block_delta`, whose payload holds
 * `delta: { type, text }` — `text_delta` for the answer, `thinking_delta` for
 * reasoning. The contract also documents a standalone `text_delta` event as a
 * convenience projection of the same fragments; it is accepted here so a client
 * works whether or not a deployment emits it, but it is not relied upon (the live
 * API sends `content_block_delta` only).
 *
 * `kind` matters: concatenating reasoning into the answer is the obvious way to
 * get a transcript that looks subtly wrong.
 */
export function deltaOf(envelope: StreamEnvelope): { kind: 'text' | 'thinking'; text: string } | null {
  if (envelope.event_type === 'text_delta') {
    const payload = envelope.payload as { text?: unknown; delta?: unknown }
    if (typeof payload.text === 'string') return { kind: 'text', text: payload.text }
    if (typeof payload.delta === 'string') return { kind: 'text', text: payload.delta }
    return null
  }
  if (envelope.event_type !== 'content_block_delta') return null
  const delta = (envelope.payload as { delta?: unknown }).delta
  if (!delta || typeof delta !== 'object') return null
  const { type, text } = delta as { type?: unknown; text?: unknown }
  if (typeof text !== 'string' || text.length === 0) return null
  if (type === 'text_delta') return { kind: 'text', text }
  if (type === 'thinking_delta') return { kind: 'thinking', text }
  return null
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
